import React, { useMemo } from "react";
import CountUp from "react-countup";
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";

/**
 * Statistics for the institution-categories screen.
 *
 * The page listed categories in a table and said nothing about them as a whole.
 * Everything here is derived from the list the page has already loaded — the
 * `/user-categories/` payload carries `subcategories_count` and `users_count`
 * per row — so the figures cost no extra request and can never disagree with
 * the table sitting underneath them.
 *
 * The reading chosen for "users" is the endpoint's own: users attached to the
 * category directly, plus those attached through one of its institutions. The
 * dashboard counts direct attachments only, so the two screens will differ for
 * a category whose users sit on its institutions — the table on this page uses
 * this same number, which is what matters for consistency here.
 */

/** Ranks categories by attached users, keeping only those that have any. */
function useRanking(categories) {
  return useMemo(() => {
    return [...categories]
      .filter((c) => (c.users_count || 0) > 0)
      .sort((a, b) => (b.users_count || 0) - (a.users_count || 0))
      .slice(0, 5);
  }, [categories]);
}

function StatCard({ label, hint, value, icon, tone }) {
  return (
    <Col xl={3} md={6}>
      <Card className="card-animate">
        <CardBody>
          <div className="d-flex align-items-center">
            <div className="flex-grow-1 overflow-hidden">
              <p className="text-uppercase fw-medium text-muted mb-0 text-truncate">
                {label}
              </p>
              {hint ? <p className="text-muted fs-11 mb-0">{hint}</p> : null}
              <h2 className="mt-4 ff-secondary fw-semibold">
                <CountUp start={0} end={value} duration={1} separator=" " />
              </h2>
            </div>
            <div className="flex-shrink-0 ms-2">
              <div
                className={`avatar-sm rounded-circle bg-${tone}-subtle d-flex align-items-center justify-content-center`}
              >
                <i className={`${icon} fs-2 text-${tone}`} />
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  );
}

function CategoriesOverview({ categories = [] }) {
  const stats = useMemo(() => {
    const totalCategories = categories.length;
    const totalInstitutions = categories.reduce(
      (sum, c) => sum + (c.subcategories_count || 0),
      0
    );
    const totalUsers = categories.reduce(
      (sum, c) => sum + (c.users_count || 0),
      0
    );
    // Categories nobody is attached to: the actionable number on this screen,
    // since an empty category is either unused or badly named.
    const emptyCategories = categories.filter(
      (c) => (c.users_count || 0) === 0
    ).length;

    return {
      totalCategories,
      totalInstitutions,
      totalUsers,
      emptyCategories,
    };
  }, [categories]);

  const ranking = useRanking(categories);
  const maxUsers = ranking.length > 0 ? ranking[0].users_count || 0 : 0;

  if (categories.length === 0) return null;

  return (
    <>
      <Row>
        <StatCard
          label="Catégories"
          hint="Blocs institutionnels"
          value={stats.totalCategories}
          icon="ri-stack-line"
          tone="primary"
        />
        <StatCard
          label="Institutions"
          hint="Toutes catégories confondues"
          value={stats.totalInstitutions}
          icon="ri-building-line"
          tone="info"
        />
        <StatCard
          label="Utilisateurs rattachés"
          hint="Directement ou via une institution"
          value={stats.totalUsers}
          icon="ri-team-line"
          tone="success"
        />
        <StatCard
          label="Catégories sans utilisateur"
          hint="À vérifier ou à supprimer"
          value={stats.emptyCategories}
          icon="ri-error-warning-line"
          tone="warning"
        />
      </Row>

      {ranking.length > 0 ? (
        <Row>
          <Col xs={12}>
            <Card>
              <CardHeader className="border-0 align-items-center d-flex">
                <h4 className="card-title mb-0 flex-grow-1">
                  Catégories les plus utilisées
                </h4>
                <span className="text-muted fs-12">
                  Top {ranking.length} sur {stats.totalCategories}
                </span>
              </CardHeader>
              <CardBody className="pt-0">
                <ul className="list-unstyled vstack gap-3 mb-0">
                  {ranking.map((category) => {
                    // Relative to the leader rather than to the total, so the
                    // shape of the distribution stays readable when one
                    // category dominates.
                    const share =
                      maxUsers > 0
                        ? Math.round(((category.users_count || 0) / maxUsers) * 100)
                        : 0;

                    return (
                      <li key={category.id}>
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <span className="fw-medium text-truncate me-2">
                            {category.name}
                            {category.user_type?.label ? (
                              <span className="badge bg-primary-subtle text-primary ms-2">
                                {category.user_type.label}
                              </span>
                            ) : null}
                          </span>
                          <span className="text-muted fs-12 flex-shrink-0">
                            {category.users_count || 0} utilisateur
                            {(category.users_count || 0) > 1 ? "s" : ""}
                            {" · "}
                            {category.subcategories_count || 0} institution
                            {(category.subcategories_count || 0) > 1 ? "s" : ""}
                          </span>
                        </div>
                        <div className="progress" style={{ height: 6 }}>
                          <div
                            className="progress-bar bg-primary"
                            role="progressbar"
                            style={{ width: `${share}%` }}
                            aria-valuenow={share}
                            aria-valuemin={0}
                            aria-valuemax={100}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </CardBody>
            </Card>
          </Col>
        </Row>
      ) : null}
    </>
  );
}

export default CategoriesOverview;
